import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Loader2, Sparkles, User, Bot, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#B08D2F";

// Localised UI strings for the Sophie section.
const L = {
  de: {
    kicker: "KI-CONCIERGE · IMMER VERFÜGBAR",
    title: "Sprechen Sie mit Sophie",
    subtitle: "Fragen zum Service, zu Preisen oder zur nächsten Route? Sophie antwortet in Ihrer Sprache — und erkennt automatisch, wenn Sie eine Reise beschreiben.",
    placeholder: "Beschreiben Sie Ihre Fahrt oder stellen Sie eine Frage…",
    send: "Senden", thinking: "Sophie denkt nach…",
    greeting: "Hallo — ich bin Sophie, Ihr persönlicher Concierge. Wie kann ich Ihnen heute helfen?",
    detected: "Reise erkannt",
    detectedBody: "Ich habe folgende Fahrt aus Ihrer Nachricht erkannt:",
    goBook: "Zur vorausgefüllten Reservierung",
    errorMsg: "Sophie ist gerade nicht erreichbar. Bitte per WhatsApp oder Telefon kontaktieren.",
    powered: "Angetrieben durch Claude Sonnet · Ihre Nachrichten werden vertraulich behandelt.",
    quickPrompts: ["Wie viel kostet ein Transfer Luzern → Zürich Flughafen?", "Ich brauche einen Chauffeur für morgen 8 Uhr.", "Können Sie Kindersitze bereitstellen?"],
  },
  en: {
    kicker: "AI CONCIERGE · ALWAYS ON",
    title: "Talk to Sophie",
    subtitle: "Questions about the service, prices or your next route? Sophie replies in your language — and detects trips automatically when you describe one.",
    placeholder: "Describe your trip or ask a question…",
    send: "Send", thinking: "Sophie is thinking…",
    greeting: "Hello — I'm Sophie, your personal concierge. How can I help you today?",
    detected: "Trip detected",
    detectedBody: "I extracted the following trip from your message:",
    goBook: "Go to pre-filled reservation",
    errorMsg: "Sophie is currently unavailable. Please reach us on WhatsApp or by phone.",
    powered: "Powered by Claude Sonnet · Your messages are handled confidentially.",
    quickPrompts: ["What's the price for a Lucerne → Zurich Airport transfer?", "I need a chauffeur tomorrow at 8 AM.", "Do you provide child seats?"],
  },
  pt: {
    kicker: "CONCIERGE IA · SEMPRE ATIVA",
    title: "Fale com a Sophie",
    subtitle: "Dúvidas sobre o serviço, preços ou a próxima viagem? A Sophie responde na sua língua — e detecta automaticamente quando você descreve uma viagem.",
    placeholder: "Descreva a sua viagem ou faça uma pergunta…",
    send: "Enviar", thinking: "A Sophie está a pensar…",
    greeting: "Olá — sou a Sophie, a sua concierge pessoal. Como posso ajudar hoje?",
    detected: "Viagem detectada",
    detectedBody: "Extraí a seguinte viagem da sua mensagem:",
    goBook: "Ir para reserva pré-preenchida",
    errorMsg: "A Sophie está temporariamente indisponível. Contacte-nos por WhatsApp ou telefone.",
    powered: "Powered by Claude Sonnet · As suas mensagens são tratadas com confidencialidade.",
    quickPrompts: ["Quanto custa um transfer Luzern → Aeroporto de Zurique?", "Preciso de um chauffeur amanhã às 8h.", "Fornecem cadeiras para crianças?"],
  },
  fr: {
    kicker: "CONCIERGE IA · TOUJOURS ACTIVE",
    title: "Parlez à Sophie",
    subtitle: "Des questions sur le service, les tarifs ou votre prochain trajet ? Sophie répond dans votre langue — et détecte automatiquement les trajets décrits.",
    placeholder: "Décrivez votre trajet ou posez une question…",
    send: "Envoyer", thinking: "Sophie réfléchit…",
    greeting: "Bonjour — je suis Sophie, votre concierge personnelle. Comment puis-je vous aider aujourd'hui ?",
    detected: "Trajet détecté",
    detectedBody: "J'ai extrait le trajet suivant de votre message :",
    goBook: "Aller à la réservation pré-remplie",
    errorMsg: "Sophie est momentanément indisponible. Contactez-nous par WhatsApp ou téléphone.",
    powered: "Propulsé par Claude Sonnet · Vos messages restent confidentiels.",
    quickPrompts: ["Combien coûte un transfert Lucerne → Aéroport de Zurich ?", "Il me faut un chauffeur demain à 8h.", "Fournissez-vous des sièges enfants ?"],
  },
  it: {
    kicker: "CONCIERGE IA · SEMPRE ATTIVA",
    title: "Parla con Sophie",
    subtitle: "Domande sul servizio, sui prezzi o sul prossimo viaggio? Sophie risponde nella tua lingua — e riconosce automaticamente i viaggi che descrivi.",
    placeholder: "Descrivi il tuo viaggio o fai una domanda…",
    send: "Invia", thinking: "Sophie sta pensando…",
    greeting: "Ciao — sono Sophie, la tua concierge personale. Come posso aiutarti oggi?",
    detected: "Viaggio rilevato",
    detectedBody: "Ho estratto il seguente viaggio dal tuo messaggio:",
    goBook: "Vai alla prenotazione precompilata",
    errorMsg: "Sophie non è al momento disponibile. Contattaci su WhatsApp o al telefono.",
    powered: "Powered by Claude Sonnet · I tuoi messaggi sono trattati con riservatezza.",
    quickPrompts: ["Quanto costa un transfer Lucerna → Aeroporto di Zurigo?", "Mi serve un chauffeur domani alle 8.", "Fornite seggiolini per bambini?"],
  },
};

const serviceLabel = (svc, lang) => {
  const m = {
    de: { airport: "Flughafentransfer", hourly: "Stundenweise", fullday: "Tageschauffeur", intercity: "Interstädt. Transfer", hotel: "Hoteltransfer", event: "Event", wedding: "Hochzeit", other: "Andere" },
    en: { airport: "Airport transfer", hourly: "Hourly", fullday: "Full-day chauffeur", intercity: "Intercity transfer", hotel: "Hotel transfer", event: "Event", wedding: "Wedding", other: "Other" },
    pt: { airport: "Transfer aeroporto", hourly: "À hora", fullday: "Dia completo", intercity: "Transfer entre cidades", hotel: "Transfer hotel", event: "Evento", wedding: "Casamento", other: "Outro" },
    fr: { airport: "Transfert aéroport", hourly: "À l'heure", fullday: "Journée complète", intercity: "Transfert intercité", hotel: "Transfert hôtel", event: "Événement", wedding: "Mariage", other: "Autre" },
    it: { airport: "Transfer aeroporto", hourly: "Ad ore", fullday: "Giornata intera", intercity: "Transfer intercittà", hotel: "Transfer hotel", event: "Evento", wedding: "Matrimonio", other: "Altro" },
  };
  return (m[lang] || m.en)[svc] || svc;
};

/**
 * Build a /reservar URL pre-filled with extracted booking fields.
 */
const buildBookingUrl = (data) => {
  const p = new URLSearchParams();
  if (data.origin) p.set("origin", data.origin);
  if (data.destination) p.set("destination", data.destination);
  if (data.date && data.time) p.set("when", `${data.date}T${data.time}`);
  else if (data.date) p.set("when", `${data.date}T09:00`);
  if (data.service) p.set("service", data.service);
  if (data.passengers) p.set("passengers", String(data.passengers));
  if (data.luggage) p.set("luggage", String(data.luggage));
  if (data.flight_number) p.set("flight", data.flight_number);
  return `/reservar?${p.toString()}`;
};

export const SophieConcierge = ({ lang = "de" }) => {
  const t = L[lang] || L.de;
  const [messages, setMessages] = useState([{ role: "assistant", content: t.greeting }]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [extractLoading, setExtractLoading] = useState(false);
  const bodyRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Refresh greeting when the site language changes.
    setMessages([{ role: "assistant", content: (L[lang] || L.de).greeting }]);
  }, [lang]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, streaming]);

  const runExtract = useCallback(async (text) => {
    setExtractLoading(true);
    try {
      const r = await fetch(`${API}/concierge/extract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, lang }),
      });
      const data = await r.json();
      if (data && data.confidence >= 0.55 && (data.origin || data.destination)) {
        setExtracted(data);
      }
    } catch { /* silent */ }
    setExtractLoading(false);
  }, [lang]);

  // Debounced auto-extract while the user types (fires 900ms after last keystroke,
  // requires at least 20 chars).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!input || input.trim().length < 20) { setExtracted(null); return; }
    debounceRef.current = setTimeout(() => { runExtract(input.trim()); }, 900);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [input, runExtract]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const nextMsgs = [...messages, { role: "user", content: text }, { role: "assistant", content: "" }];
    setMessages(nextMsgs);
    setInput("");
    setStreaming(true);
    // Also run extraction once (fire-and-forget) on the sent message.
    runExtract(text);

    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      if (!res.body) throw new Error("no-body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const lines = chunk.split("\n");
          const eventLine = lines.find((l) => l.startsWith("event: "));
          const dataLine = lines.find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          const payload = dataLine.slice(6);
          try {
            const parsed = JSON.parse(payload);
            const eventName = eventLine ? eventLine.slice(7) : "message";
            if (eventName === "session" && parsed.session_id) setSessionId(parsed.session_id);
            else if (parsed.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, content: (last.content || "") + parsed.delta };
                return copy;
              });
            }
          } catch { /* ignore malformed lines */ }
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) copy[copy.length - 1] = { ...last, content: t.errorMsg };
        return copy;
      });
    }
    setStreaming(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const quickPrompt = (p) => setInput(p);

  return (
    <div
      data-testid="sophie-concierge"
      className="border border-brand-border bg-brand-surface overflow-hidden"
      style={{ background: "linear-gradient(180deg, rgba(176,141,47,0.04), transparent 40%), var(--brand-surface, #101010)" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-0">
        {/* LEFT — persona intro */}
        <div className="p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-brand-border relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-full border flex items-center justify-center" style={{ borderColor: GOLD, color: GOLD }}>
              <Sparkles size={18} />
            </div>
            <div>
              <p className="text-[10px] tracking-widest2 uppercase" style={{ color: GOLD }}>— {t.kicker}</p>
              <p className="text-brand-platinum text-xs mt-1">Sophie · SwissAlpenLine</p>
            </div>
          </div>
          <h3 className="display text-3xl md:text-4xl text-brand-platinum leading-tight mb-4">{t.title}</h3>
          <p className="text-brand-silver text-sm leading-relaxed mb-6">{t.subtitle}</p>
          <div className="space-y-2">
            {t.quickPrompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => quickPrompt(p)}
                data-testid="sophie-quick-prompt"
                className="w-full text-left border border-brand-border px-4 py-3 text-xs text-brand-silver hover:border-bronze hover:text-brand-platinum transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
          <p className="text-[10px] tracking-widest2 uppercase text-brand-dim mt-8">{t.powered}</p>
        </div>

        {/* RIGHT — chat */}
        <div className="flex flex-col" style={{ minHeight: 540 }}>
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-5" data-testid="sophie-chat-body" style={{ maxHeight: 540 }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className="w-8 h-8 rounded-full border border-brand-border flex items-center justify-center shrink-0 mt-0.5"
                     style={{ color: m.role === "user" ? "#e8e8e8" : GOLD }}>
                  {m.role === "user" ? <User size={13} /> : <Bot size={13} />}
                </div>
                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand-bg border border-brand-border text-brand-platinum" : "text-brand-silver"}`}
                     data-testid={m.role === "user" ? "sophie-msg-user" : "sophie-msg-assistant"}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="opacity-60">···</span> : "")}
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex items-center gap-2 text-xs text-brand-dim pl-11">
                <Loader2 size={12} className="animate-spin" /> {t.thinking}
              </div>
            )}
            {extracted && (
              <div className="border p-4 fadeUp" style={{ borderColor: GOLD, background: "rgba(176,141,47,0.06)" }} data-testid="sophie-extracted-panel">
                <p className="text-[10px] tracking-widest2 uppercase mb-2" style={{ color: GOLD }}>— {t.detected}</p>
                <p className="text-brand-silver text-xs mb-3">{t.detectedBody}</p>
                <ul className="space-y-1 text-xs text-brand-platinum mb-4">
                  {extracted.origin && <li><span className="text-brand-silver">→</span> {extracted.origin}</li>}
                  {extracted.destination && <li><span className="text-brand-silver">⇢</span> {extracted.destination}</li>}
                  {extracted.date && <li><span className="text-brand-silver">◷</span> {extracted.date}{extracted.time ? ` · ${extracted.time}` : ""}</li>}
                  {extracted.service && <li><span className="text-brand-silver">◇</span> {serviceLabel(extracted.service, lang)}</li>}
                  {extracted.passengers && <li><span className="text-brand-silver">◈</span> {extracted.passengers} pax</li>}
                </ul>
                <Link to={buildBookingUrl(extracted)} data-testid="sophie-go-book" className="btn-pill btn-pill--solid inline-flex text-xs">
                  {t.goBook} <ArrowRight size={12} />
                </Link>
              </div>
            )}
          </div>

          {/* input */}
          <div className="border-t border-brand-border p-4 md:p-5 bg-brand-bg">
            <div className="flex gap-3 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={2}
                placeholder={t.placeholder}
                data-testid="sophie-input"
                className="flex-1 bg-brand-surface border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-bronze focus:outline-none resize-none"
                disabled={streaming}
              />
              <button
                onClick={send}
                disabled={!input.trim() || streaming}
                data-testid="sophie-send"
                className="btn-pill btn-pill--solid shrink-0 self-stretch justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {streaming ? <Loader2 size={14} className="animate-spin" /> : <><span className="hidden md:inline">{t.send}</span> <Send size={14} /></>}
              </button>
            </div>
            {extractLoading && !extracted && (
              <p className="text-[10px] tracking-widest2 uppercase text-brand-dim mt-2 flex items-center gap-2">
                <Loader2 size={10} className="animate-spin" /> Sophie · scanning message…
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SophieConcierge;
